import { gql, useQuery } from '@apollo/client';
import IconDoubleChevronDown from '@apollo/icons/small/IconDoubleChevronDown.svg';
import IconOverflow from '@apollo/icons/small/IconOverflow.svg';
import IconPlus from '@apollo/icons/small/IconPlus.svg';
import {
  IconButton,
  Menu,
  MenuButton,
  MenuContent,
  MenuGroup,
  MenuItem,
  MenuList,
} from '@apollo/orbit';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  getClientRect,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import React, { useCallback } from 'react';
import ReactResizeDetector from 'react-resize-detector';

import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { scrollToElementIfNotInView } from 'src/lib/scrollTo';

import {
  EditorTabState,
  useTabbedDisplayNames,
  useTabbedSavedOperationLocalState,
} from '../hooks/useExplorerState/useEditorTabState/useEditorTabState';
import { useHeadersManagerContext } from '../hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';

import { explorerTabsDndKeyboardCoordinateGetter } from './explorerTabsDndKeyboardCoordinateGetter';
import { Tab } from './Tab';

export const ExplorerTabs = ({
  editorTabState,
}: {
  editorTabState: EditorTabState;
}) => {
  const {
    selectedTab,
    setSelectedTab,
    tabIds,
    setTabIds,
    closeTab,
    createNewTab,
  } = editorTabState;

  const [operationIdBeingDragged, setOperationIdBeingDragged] =
    React.useState<string>();

  const { tabbedDisplayNames } = useTabbedDisplayNames();
  const { savedOperationLocalState } = useTabbedSavedOperationLocalState();
  const { tabbedHeaderDefinitions } = useHeadersManagerContext();

  const savedCollectionEntryIds = React.useMemo(
    () =>
      tabIds.flatMap(
        (tabId) => savedOperationLocalState[tabId]?.collectionEntryId ?? [],
      ),
    [tabIds, savedOperationLocalState],
  );
  const { data } = useQuery<
    GraphQLTypes.GetCollectionEntries,
    GraphQLTypes.GetCollectionEntriesVariables
  >(
    gql`
      query GetCollectionEntries($collectionEntryIds: [ID!]!) {
        operationCollectionEntries(collectionEntryIds: $collectionEntryIds) {
          id
          name
          collection {
            id
            name
            isSandbox
            isShared
            variants {
              id
              graphId
              name
            }
            permissions {
              canEditOperations
            }
          }
        }
      }
    `,
    {
      variables: {
        collectionEntryIds: savedCollectionEntryIds,
      },
    },
  );
  // When this refetches it does so with new variables so the cache will have
  // no result. This keeps around the last result until the new one loads
  const [lastCollectionEntries, setLastCollectionEntries] = React.useState(
    data?.operationCollectionEntries,
  );
  React.useEffect(() => {
    if (data?.operationCollectionEntries) {
      setLastCollectionEntries(data?.operationCollectionEntries);
    }
  }, [data]);
  const savedEntryDetailsMap = Object.fromEntries(
    (lastCollectionEntries ?? []).map((collectionEntry) => [
      collectionEntry.id,
      {
        name: collectionEntry.name,
        canEditSavedEntryCollection:
          collectionEntry.collection.permissions.canEditOperations,
        collection: collectionEntry.collection,
      },
    ]),
  );

  const onDragEnd = React.useCallback(
    ({
      active,
      over,
    }: DragEndEvent & {
      over: null | { data?: { current?: { sortable?: { index: number } } } };
    }) => {
      const operationId = active.id;
      const startIndex = tabIds.findIndex((tabId) => tabId === operationId);
      const movedTab = tabIds[startIndex];
      const targetData = over?.data.current?.sortable;

      const endIndex = targetData?.index;
      if (endIndex === undefined || !movedTab) {
        // for some reason, this became necessary to make tap-to-click work when selecting a tab
        // the onclick on the tab doesn't ever seem to fire now, hijacked by drag and drop
        // a difference between tap-to-click and haptic clicking is that "end" is null for a tap-to-click
        setSelectedTab(operationId);
        return;
      }

      const newTabIds = [...tabIds];
      newTabIds.splice(startIndex, 1);
      newTabIds.splice(endIndex, 0, movedTab);
      setTabIds(newTabIds);
      setSelectedTab(operationId);
      setOperationIdBeingDragged(undefined);
    },
    [tabIds, setTabIds, setSelectedTab],
  );

  const onDragStart = React.useCallback(({ active }: DragStartEvent) => {
    setOperationIdBeingDragged(active.id);
  }, []);

  const tabsContainerRef = React.useRef<HTMLUListElement | null>(null);
  const tabsContainerElement = tabsContainerRef.current;
  const [tabsOverflowing, setTabsOverflowing] = React.useState(false);
  const refreshIsOverflowing = React.useCallback(
    () =>
      setTabsOverflowing(
        tabsContainerElement !== null &&
          tabsContainerElement.offsetWidth < tabsContainerElement.scrollWidth,
      ),
    [tabsContainerElement],
  );
  React.useEffect(() => refreshIsOverflowing());

  React.useEffect(() => {
    const tabNodes = tabsContainerRef.current?.children;
    if (selectedTab && tabNodes) {
      const tabIndex = tabIds.indexOf(selectedTab);
      const tabNode = tabNodes[tabIndex];
      if (tabNode) {
        scrollToElementIfNotInView(tabNode, { inline: 'nearest' }, true);
      }
    }
  }, [selectedTab, tabIds]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: explorerTabsDndKeyboardCoordinateGetter,
    }),
  );

  // TODO(tim): this should probably be implemented in the EditorTabState
  const closeAllTabs = useCallback(() => {
    tabIds.forEach((tabId) => closeTab(tabId));
    setSelectedTab(''); // triggers recreation of the default tab
  }, [tabIds, closeTab, setSelectedTab]);

  return (
    <>
      <DndContext
        measuring={{
          droppable: {
            measure: (element) => {
              const parent = element.parentElement;
              if (!parent) return getClientRect(element);
              return getClientRect(parent, {
                ignoreTransform: true,
              });
            },
          },
        }}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}
        onDragStart={onDragStart}
        screenReaderInstructions={{
          draggable: `To select a tab in this list of Explorer tabs, click escape or space twice.
To pick up a tab in preparation to reposition it in your list of tabs, press escape or space.
While dragging, use the arrow keys to move the tab in any given direction.
Press escape or space again to drop the tab in its new position, or press escape to cancel.`,
        }}
        sensors={sensors}
      >
        <ReactResizeDetector handleWidth onResize={refreshIsOverflowing}>
          <ul
            ref={tabsContainerRef}
            className="hide-scrollbar relative flex items-start gap-2 overflow-x-scroll pt-2"
            role="tablist"
          >
            <SortableContext
              items={tabIds}
              strategy={horizontalListSortingStrategy}
            >
              {tabIds.map((operationId) => {
                const isSelected = operationId === selectedTab;

                const savedOperationLocalStateEntry =
                  savedOperationLocalState[operationId];

                const unsavedDisplayName =
                  tabbedDisplayNames[operationId] ?? 'Unnamed';

                const savedEntryDetails =
                  savedOperationLocalStateEntry?.collectionEntryId === undefined
                    ? undefined
                    : savedEntryDetailsMap[
                        savedOperationLocalStateEntry.collectionEntryId
                      ];

                return (
                  <Tab
                    closeTab={closeTab}
                    isSelected={isSelected}
                    operationId={operationId}
                    savedEntryDetails={savedEntryDetails}
                    savedOperationLocalStateEntry={
                      savedOperationLocalStateEntry
                    }
                    setSelectedTab={setSelectedTab}
                    unsavedDisplayName={unsavedDisplayName}
                    isBeingDragged={operationIdBeingDragged === operationId}
                    key={operationId}
                  />
                );
              })}
            </SortableContext>

            {
              // the dropAnimation here flashes no tab name while the dropAnimation is happening
              // since we have an invisible overlay, the duration needs to be 0
            }
            <DragOverlay dropAnimation={{ duration: 0, easing: 'ease' }}>
              {
                // The drag overlay is the actual element that follows your cursor.
                // We chose a transparent div for this, because we don't
                // want an overlay in addition to the actual tab moving, but
                // we do want a dom element that matches the width and height of
                // the tab being dragged, so we can apply the grabbing cursor as you move.
              }
              <div
                // NOTE: keep this width / height in line with the tab width height in Tab.tsx
                // and update the margins below if the padding on the tabs changes
                // margin top is negative to account for padding around <button element which is the draggable item
                // margin left is negative to align drag overlay exactly with tab
                className="h-10 cursor-grabbing"
              >
                <div className="invisible" />
              </div>
            </DragOverlay>
          </ul>
        </ReactResizeDetector>
      </DndContext>
      {tabsOverflowing && (
        <Menu placement="bottom-end" autoSelect={false}>
          <Tooltip label="Show all workspaces">
            <MenuButton
              as={IconButton}
              variant="hidden"
              className="mt-2"
              size="sm"
              aria-label="Show all workspaces"
            >
              <IconDoubleChevronDown />
            </MenuButton>
          </Tooltip>
          <MenuContent>
            <MenuList>
              {tabIds.map((operationId) => {
                const savedOperationLocalStateEntry =
                  savedOperationLocalState[operationId];
                const savedEntryDetails =
                  savedOperationLocalStateEntry?.collectionEntryId === undefined
                    ? undefined
                    : savedEntryDetailsMap[
                        savedOperationLocalStateEntry.collectionEntryId
                      ];

                const tabName =
                  savedEntryDetails?.name ??
                  tabbedDisplayNames[operationId] ??
                  'Unnamed';
                return (
                  <MenuItem
                    key={operationId}
                    onClick={() => setSelectedTab(operationId)}
                  >
                    {tabName}
                  </MenuItem>
                );
              })}
            </MenuList>
          </MenuContent>
        </Menu>
      )}
      <Tooltip label="New workspace">
        <IconButton
          variant="hidden"
          onClick={() =>
            createNewTab({
              headers: tabbedHeaderDefinitions,
            })
          }
          className="mt-2"
          size="sm"
          icon={<IconPlus />}
          aria-label="Create New Workspace"
          data-testid="new_tab"
        />
      </Tooltip>
      {tabsOverflowing && (
        <Menu>
          <Tooltip label="More actions...">
            <MenuButton
              as={IconButton}
              variant="hidden"
              className="mt-2"
              size="sm"
              icon={<IconOverflow />}
              aria-label="More actions"
              data-testid="actions"
            />
          </Tooltip>
          <MenuContent className="w-auto min-w-0 p-2">
            <MenuGroup>
              <MenuItem
                onClick={closeAllTabs}
                aria-label="Close All"
                data-testid="close_all"
              >
                Close All
              </MenuItem>
            </MenuGroup>
          </MenuContent>
        </Menu>
      )}
    </>
  );
};
